/**
 * Real data, transcribed programmatically from Climate Risk Assessment.xlsx,
 * sheet "Vulnerability", rows 24-35 (Energy) and 39-49 (Agriculture) -
 * "Physical asset impact" table. Generated by
 * script/parse-vulnerability-excel.ts - do not hand-edit; re-run the script
 * against an updated sheet instead.
 *
 * Replaces shared/seed-data/vulnerability-defaults-placeholder.json
 * (AI-generated placeholder, removed 2026-09-12).
 *
 * Keyed by (sector, assetTypeName, hazard) as free text, NOT a foreign key
 * to asset_types.id - deliberate, see shared/climate-risk-schema.ts's
 * assetTypeVulnerabilityDefaults comment: asset_types is still incomplete
 * and splits Energy into 2 sub-sectors that this sheet doesn't distinguish
 * (functional spec open items #4/#5) - resolving that is out of scope here.
 * Assets whose type isn't among the 23 names below (23 total: 12 energy + 11
 * agriculture) simply get no default and degrade to "pending", same as any
 * other missing-data case.
 */

export interface AssetTypeVulnerabilityDefaultSeed {
  sector: "energy" | "agriculture";
  assetTypeName: string;
  hazard: string;
  score: 0 | 1 | 2 | 3 | 4;
  justification: string;
}

export const ASSET_TYPE_VULNERABILITY_DEFAULTS: readonly AssetTypeVulnerabilityDefaultSeed[] = [
  {
    sector: "energy",
    assetTypeName: "Exploration site",
    hazard: "Heat Stress",
    score: 1,
    justification: "Exploration equipment and temporary camp structures are mobile and low-cost; heat causes wear but rarely requires significant repair investment.",
  },
  {
    sector: "energy",
    assetTypeName: "Exploration site",
    hazard: "Changing Precipitation",
    score: 1,
    justification: "Temporary camp infrastructure and mobile equipment sustain little lasting physical damage from rainfall variability.",
  },
  {
    sector: "energy",
    assetTypeName: "Exploration site",
    hazard: "Water Stress",
    score: 1,
    justification: "No physical assets at this stage are damaged by reduced water availability.",
  },
  {
    sector: "energy",
    assetTypeName: "Exploration site",
    hazard: "Drought",
    score: 1,
    justification: "Drought causes negligible physical wear to exploration equipment or camp infrastructure.",
  },
  {
    sector: "energy",
    assetTypeName: "Exploration site",
    hazard: "Floods",
    score: 3,
    justification: "Unpaved access roads and temporary camp structures - the norm at this stage - can be washed out or destroyed, requiring re-grading of roads and rebuilding of camp facilities.",
  },
  {
    sector: "energy",
    assetTypeName: "Exploration site",
    hazard: "Landslide",
    score: 2,
    justification: "Mobile rigs and light camp structures can be damaged or buried, but replacement and reinstatement cost is relatively low given the low capital intensity of this asset type.",
  },
  {
    sector: "energy",
    assetTypeName: "Production facility",
    hazard: "Heat Stress",
    score: 2,
    justification: "Seals, gaskets and electrical components in wellhead equipment degrade faster under sustained heat, increasing routine maintenance and part-replacement costs.",
  },
  {
    sector: "energy",
    assetTypeName: "Production facility",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Corrosion and erosion around foundations and cabling increase with rainfall variability, adding to maintenance capex over time.",
  },
  {
    sector: "energy",
    assetTypeName: "Production facility",
    hazard: "Water Stress",
    score: 1,
    justification: "Constrained water supply does not directly damage extraction equipment.",
  },
  {
    sector: "energy",
    assetTypeName: "Production facility",
    hazard: "Drought",
    score: 1,
    justification: "Drought does not directly damage the physical structure or equipment at production sites.",
  },
  {
    sector: "energy",
    assetTypeName: "Production facility",
    hazard: "Floods",
    score: 3,
    justification: "Inundation of at-grade well pads can damage electrical systems, pumps and control equipment, requiring significant repair or replacement.",
  },
  {
    sector: "energy",
    assetTypeName: "Production facility",
    hazard: "Landslide",
    score: 4,
    justification: "Structural damage to wellhead infrastructure can be severe and safety-critical, requiring major, costly and slow engineering repair before the facility can safely restart.",
  },
  {
    sector: "energy",
    assetTypeName: "Refinery",
    hazard: "Heat Stress",
    score: 2,
    justification: "Refineries are engineered for high-temperature process operations and have relatively better inherent heat tolerance, though seals, insulation and instrumentation still need more frequent servicing.",
  },
  {
    sector: "energy",
    assetTypeName: "Refinery",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Regional refineries tend to be older with some maintenance backlog, which can reduce drainage resilience and increase repair needs after intense rainfall.",
  },
  {
    sector: "energy",
    assetTypeName: "Refinery",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity, though process units may need protective shutdown procedures.",
  },
  {
    sector: "energy",
    assetTypeName: "Refinery",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought to the refinery structure itself.",
  },
  {
    sector: "energy",
    assetTypeName: "Refinery",
    hazard: "Floods",
    score: 3,
    justification: "Ageing infrastructure and deferred maintenance can leave drainage and containment systems under-performing in extreme events, risking damage to ground-level electrical and process equipment.",
  },
  {
    sector: "energy",
    assetTypeName: "Refinery",
    hazard: "Landslide",
    score: 4,
    justification: "A refinery is one of the most capital-intensive and safety-critical asset types in the value chain; a structural impact would require extremely costly, complex and slow engineering repair.",
  },
  {
    sector: "energy",
    assetTypeName: "Power generation facility",
    hazard: "Heat Stress",
    score: 2,
    justification: "Turbines, generators and solar arrays are industrially engineered for regional operating conditions, though cooling systems and electrical components still require more frequent servicing under sustained heat.",
  },
  {
    sector: "energy",
    assetTypeName: "Power generation facility",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Intake structures and civil works require more frequent inspection and repair as inflow patterns and sediment loads shift with changing rainfall.",
  },
  {
    sector: "energy",
    assetTypeName: "Power generation facility",
    hazard: "Water Stress",
    score: 1,
    justification: "Reduced water availability constrains output but does not directly damage the physical plant.",
  },
  {
    sector: "energy",
    assetTypeName: "Power generation facility",
    hazard: "Drought",
    score: 1,
    justification: "Drought constrains output but causes negligible direct physical damage to generation infrastructure.",
  },
  {
    sector: "energy",
    assetTypeName: "Power generation facility",
    hazard: "Floods",
    score: 3,
    justification: "Major dam infrastructure is generally engineered with flood-routing capacity, reducing structural vulnerability, but non-hydro plant types (thermal, geothermal) and ancillary civil works typically have less flood-specific protection and can sustain damage requiring repair.",
  },
  {
    sector: "energy",
    assetTypeName: "Power generation facility",
    hazard: "Landslide",
    score: 4,
    justification: "Dam abutments, geothermal wellfields and turbine halls are highly engineered but also highly capital-intensive; a structural impact could raise dam-safety or plant-integrity concerns and require extremely costly, slow engineering repair.",
  },
  {
    sector: "energy",
    assetTypeName: "Substation",
    hazard: "Heat Stress",
    score: 2,
    justification: "Transformers and switchgear run hotter and their insulating oil degrades faster under sustained heat, increasing the frequency of oil testing, cooling-system servicing and component replacement.",
  },
  {
    sector: "energy",
    assetTypeName: "Substation",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Corrosion of steel structures and switchgear housings accelerates with more variable rainfall, and drainage/grounding systems at typical open-yard substations need more frequent repair.",
  },
  {
    sector: "energy",
    assetTypeName: "Substation",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity.",
  },
  {
    sector: "energy",
    assetTypeName: "Substation",
    hazard: "Drought",
    score: 2,
    justification: "Dust accumulation on insulators during prolonged dry periods is a recognised regional cause of flashover and equipment damage, requiring more frequent cleaning and occasional insulator replacement.",
  },
  {
    sector: "energy",
    assetTypeName: "Substation",
    hazard: "Floods",
    score: 4,
    justification: "Transformers, switchgear and control equipment are typically installed at grade in open yards; inundation can cause short-circuits and require replacement of transformers and switchgear, which are costly, often imported, and slow to procure.",
  },
  {
    sector: "energy",
    assetTypeName: "Substation",
    hazard: "Landslide",
    score: 4,
    justification: "A substation is a capital-intensive, grid-critical node concentrating high-value equipment in one location; structural damage can mean extended replacement lead times for transformers and switchgear, with limited redundancy in many regional networks.",
  },
  {
    sector: "energy",
    assetTypeName: "Processing plant",
    hazard: "Heat Stress",
    score: 2,
    justification: "Uninsulated steel-framed processing sheds and equipment housings experience accelerated wear under sustained heat, raising maintenance costs.",
  },
  {
    sector: "energy",
    assetTypeName: "Processing plant",
    hazard: "Changing Precipitation",
    score: 1,
    justification: "Solid industrial construction typical of processing plants limits physical damage from rainfall variability, beyond routine drainage upkeep.",
  },
  {
    sector: "energy",
    assetTypeName: "Processing plant",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage to plant structure or equipment from reduced water availability.",
  },
  {
    sector: "energy",
    assetTypeName: "Processing plant",
    hazard: "Drought",
    score: 1,
    justification: "Drought has negligible direct physical impact on the processing plant itself.",
  },
  {
    sector: "energy",
    assetTypeName: "Processing plant",
    hazard: "Floods",
    score: 3,
    justification: "Ground-level equipment and electrical systems can be damaged by inundation, requiring cleaning, drying, part replacement and, in severe cases, re-commissioning.",
  },
  {
    sector: "energy",
    assetTypeName: "Processing plant",
    hazard: "Landslide",
    score: 4,
    justification: "Heavy, specialised processing equipment is costly and slow to repair or replace, and structural damage to the building can require extensive rebuilding.",
  },
  {
    sector: "energy",
    assetTypeName: "Pipeline",
    hazard: "Heat Stress",
    score: 2,
    justification: "Thermal expansion increases mechanical stress on pipeline joints, adding to routine repair needs, particularly on older sections.",
  },
  {
    sector: "energy",
    assetTypeName: "Pipeline",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Accelerated corrosion of exposed steelwork and erosion around pipeline crossings raises ongoing maintenance and repair costs.",
  },
  {
    sector: "energy",
    assetTypeName: "Pipeline",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage to pipeline infrastructure from water scarcity.",
  },
  {
    sector: "energy",
    assetTypeName: "Pipeline",
    hazard: "Drought",
    score: 2,
    justification: "Soil shrinkage in clay-rich ground during extreme drought can stress buried pipeline joints and welds, occasionally requiring localised repair.",
  },
  {
    sector: "energy",
    assetTypeName: "Pipeline",
    hazard: "Floods",
    score: 4,
    justification: "Scour and washout at river crossings and exposed sections is a well-documented cause of pipeline failure, requiring substantial and often urgent repair investment.",
  },
  {
    sector: "energy",
    assetTypeName: "Pipeline",
    hazard: "Landslide",
    score: 4,
    justification: "A severed pipeline segment requires significant capital to repair, particularly where access for heavy equipment in difficult terrain slows and raises the cost of the repair, and leak/spill risk adds environmental remediation cost.",
  },
  {
    sector: "energy",
    assetTypeName: "Storage facility",
    hazard: "Heat Stress",
    score: 3,
    justification: "Un-insulated steel tanks and battery enclosures suffer accelerated material fatigue and elevated fire risk under sustained heat, often requiring inspection, seal replacement or cooling-system upgrades.",
  },
  {
    sector: "energy",
    assetTypeName: "Storage facility",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Basic bunding and drainage typical of regional tank farms may need reinforcement or repair after intense rainfall events.",
  },
  {
    sector: "energy",
    assetTypeName: "Storage facility",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage to storage infrastructure from water scarcity.",
  },
  {
    sector: "energy",
    assetTypeName: "Storage facility",
    hazard: "Drought",
    score: 1,
    justification: "Drought has negligible direct physical effect on storage tanks or battery installations.",
  },
  {
    sector: "energy",
    assetTypeName: "Storage facility",
    hazard: "Floods",
    score: 3,
    justification: "Flooding can displace or float tanks and compromise bunding integrity, requiring structural repair and re-certification before the facility can resume safe operation.",
  },
  {
    sector: "energy",
    assetTypeName: "Storage facility",
    hazard: "Landslide",
    score: 3,
    justification: "Structural damage to tanks or bunding can trigger a containment failure, requiring both physical rebuilding and regulatory re-clearance before operations can resume.",
  },
  {
    sector: "energy",
    assetTypeName: "LNG / LPG terminal",
    hazard: "Heat Stress",
    score: 2,
    justification: "Refrigeration and containment systems are engineered for regional climate conditions, but sustained extreme heat still raises servicing needs for cooling systems.",
  },
  {
    sector: "energy",
    assetTypeName: "LNG / LPG terminal",
    hazard: "Changing Precipitation",
    score: 1,
    justification: "As a high-value, safety-critical facility, terminals are typically built to stronger drainage and engineering standards than average regional industrial assets, limiting physical damage from rainfall variability.",
  },
  {
    sector: "energy",
    assetTypeName: "LNG / LPG terminal",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity to the terminal structure itself.",
  },
  {
    sector: "energy",
    assetTypeName: "LNG / LPG terminal",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought to the terminal structure itself.",
  },
  {
    sector: "energy",
    assetTypeName: "LNG / LPG terminal",
    hazard: "Floods",
    score: 3,
    justification: "Terminals are typically sited near ports or coastal/waterside locations for shipping access; even well-engineered marine-adjacent infrastructure can suffer damage to ground-level systems in storm-surge or extreme flood events.",
  },
  {
    sector: "energy",
    assetTypeName: "LNG / LPG terminal",
    hazard: "Landslide",
    score: 4,
    justification: "As one of the highest-value, most safety-critical asset types, any structural impact would require very high-cost, complex and slow restoration given the potential for a major safety incident.",
  },
  {
    sector: "energy",
    assetTypeName: "Office / headquarters",
    hazard: "Heat Stress",
    score: 1,
    justification: "Corporate offices are typically solid concrete or block construction and sustain no material damage from heat.",
  },
  {
    sector: "energy",
    assetTypeName: "Office / headquarters",
    hazard: "Changing Precipitation",
    score: 1,
    justification: "Well-engineered urban commercial buildings sustain minimal physical damage from rainfall variability.",
  },
  {
    sector: "energy",
    assetTypeName: "Office / headquarters",
    hazard: "Water Stress",
    score: 1,
    justification: "No physical damage pathway from water scarcity.",
  },
  {
    sector: "energy",
    assetTypeName: "Office / headquarters",
    hazard: "Drought",
    score: 1,
    justification: "No physical damage pathway from drought.",
  },
  {
    sector: "energy",
    assetTypeName: "Office / headquarters",
    hazard: "Floods",
    score: 2,
    justification: "Ground-floor and basement office space in flood-prone urban districts can suffer water damage to finishes, wiring and equipment, requiring localised repair.",
  },
  {
    sector: "energy",
    assetTypeName: "Office / headquarters",
    hazard: "Landslide",
    score: 1,
    justification: "Solid, engineered, typically multi-storey construction in flatter commercial districts sustains limited damage even in the rare event of an impact.",
  },
  {
    sector: "energy",
    assetTypeName: "Transmission line",
    hazard: "Heat Stress",
    score: 2,
    justification: "Conductor sag and thermal stress on hardware is a recognised maintenance issue, particularly on older lines, occasionally requiring component replacement.",
  },
  {
    sector: "energy",
    assetTypeName: "Transmission line",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Increased corrosion of towers/conductors and higher risk of insulator flashover accompany more intense or variable rainfall, raising maintenance repair needs.",
  },
  {
    sector: "energy",
    assetTypeName: "Transmission line",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity.",
  },
  {
    sector: "energy",
    assetTypeName: "Transmission line",
    hazard: "Drought",
    score: 2,
    justification: "Dry vegetation near line corridors raises wildfire risk, which can damage poles, towers and conductors, requiring repair.",
  },
  {
    sector: "energy",
    assetTypeName: "Transmission line",
    hazard: "Floods",
    score: 3,
    justification: "Tower foundations, and especially the wooden poles still common on many regional distribution and sub-transmission lines (less robust than steel lattice towers used on major transmission corridors), are vulnerable to undermining and washout, requiring pole/tower replacement.",
  },
  {
    sector: "energy",
    assetTypeName: "Transmission line",
    hazard: "Landslide",
    score: 4,
    justification: "A collapsed transmission tower or pole line requires significant capital to repair or replace, particularly where access for heavy equipment in difficult terrain slows and raises the cost of the repair.",
  },
  {
    sector: "energy",
    assetTypeName: "Selling point",
    hazard: "Heat Stress",
    score: 2,
    justification: "Forecourt canopies, dispensing equipment and small kiosk buildings typical of regional fuel/gas retail outlets tolerate heat reasonably well given outdoor-rated design, though seals and electronic components need more frequent servicing.",
  },
  {
    sector: "energy",
    assetTypeName: "Selling point",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Basic forecourt drainage and kiosk roofing can allow water ingress under intense rainfall, requiring periodic repair of roofing and drainage channels.",
  },
  {
    sector: "energy",
    assetTypeName: "Selling point",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity.",
  },
  {
    sector: "energy",
    assetTypeName: "Selling point",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought.",
  },
  {
    sector: "energy",
    assetTypeName: "Selling point",
    hazard: "Floods",
    score: 3,
    justification: "Underground fuel storage tanks and at-grade electrical dispensing equipment are vulnerable to water ingress and contamination during flooding, requiring inspection, cleaning and potential equipment replacement before the site can safely reopen.",
  },
  {
    sector: "energy",
    assetTypeName: "Selling point",
    hazard: "Landslide",
    score: 3,
    justification: "Damage to underground tanks or dispensing infrastructure carries both a repair cost and a contamination/safety risk, requiring careful and often costly remediation before operations can resume.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agricultural land",
    hazard: "Heat Stress",
    score: 2,
    justification: "Sustained heat degrades soil structure and organic matter, and can damage light on-farm infrastructure such as fencing and shade structures over time, requiring periodic renewal rather than major capital repair.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agricultural land",
    hazard: "Changing Precipitation",
    score: 3,
    justification: "More intense or erratic rainfall accelerates erosion of unterraced, un-bunded slopes - the regional norm - requiring recurring earthworks to restore usable farmland.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agricultural land",
    hazard: "Water Stress",
    score: 1,
    justification: "Land itself sustains no direct physical damage from reduced water availability, beyond gradual soil degradation over successive dry seasons.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agricultural land",
    hazard: "Drought",
    score: 2,
    justification: "Extended drought can cause soil cracking and structural degradation, occasionally requiring soil-rehabilitation investment (organic matter replenishment, re-contouring) before the land is fully productive again.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agricultural land",
    hazard: "Floods",
    score: 3,
    justification: "Floods erode topsoil, deposit debris and can damage field bunds and terracing where present, requiring earthworks repair before the land is fully usable.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agricultural land",
    hazard: "Landslide",
    score: 4,
    justification: "A landslide strips topsoil and buries the field in debris; restoring the land to productive condition can require substantial earthworks investment and multiple seasons, or in severe cases be effectively permanent.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Irrigation infrastructure",
    hazard: "Heat Stress",
    score: 1,
    justification: "Heat increases evaporation losses from unlined canals but causes little direct physical damage to the infrastructure itself.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Irrigation infrastructure",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "More intense rainfall erodes unlined earthen canal banks and can silt up intake structures, requiring periodic desilting and bank repair.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Irrigation infrastructure",
    hazard: "Water Stress",
    score: 1,
    justification: "Reduced water availability does not physically damage the infrastructure, only its utilisation.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Irrigation infrastructure",
    hazard: "Drought",
    score: 2,
    justification: "Extended dry periods can crack and degrade unlined earthen channels, requiring resealing or rebuilding before the next irrigation season.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Irrigation infrastructure",
    hazard: "Floods",
    score: 4,
    justification: "Earthen canals, low-cost intake weirs and small pump stations - the regional norm - are easily breached, silted or washed away by flood flows, typically requiring full reconstruction of the affected section.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Irrigation infrastructure",
    hazard: "Landslide",
    score: 3,
    justification: "Canal alignments and pump installations in hillside schemes can be severed or buried; rebuilding an earthen or basic piped system is comparatively low-cost, but design standards rarely account for slope failure.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Protected structure",
    hazard: "Heat Stress",
    score: 2,
    justification: "Sustained heat degrades polythene sheeting and plastic components faster than their normal replacement cycle, adding to maintenance costs.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Protected structure",
    hazard: "Changing Precipitation",
    score: 1,
    justification: "The structure's covering is designed to shed rainfall, so it sustains limited direct physical damage from rainfall variability.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Protected structure",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway to the structure itself from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Protected structure",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Protected structure",
    hazard: "Floods",
    score: 4,
    justification: "Structures are typically erected on simple post foundations at grade without raised flooring; water ingress can undermine posts, destroy the plastic covering and require substantial rebuilding.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Protected structure",
    hazard: "Landslide",
    score: 3,
    justification: "Lightweight single-storey wood or metal frame construction offers no structural resistance to ground movement and would likely be a total loss if struck, though rebuild cost is lower than for a masonry facility.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Storage facility",
    hazard: "Heat Stress",
    score: 2,
    justification: "Corrugated-iron roofing common on regional warehouses degrades faster under sustained heat, requiring periodic re-sheeting.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Storage facility",
    hazard: "Changing Precipitation",
    score: 3,
    justification: "Roof and wall-junction leaks typical of standard warehouse construction let in moisture, requiring roofing and wall repair as well as stock write-off from mould damage.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Storage facility",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Storage facility",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Storage facility",
    hazard: "Floods",
    score: 4,
    justification: "Ground-level warehouses without a raised plinth - the regional norm - suffer structural water damage to flooring, walls and electrical systems, together requiring substantial repair investment.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Storage facility",
    hazard: "Landslide",
    score: 3,
    justification: "Block or brick-walled warehouses suffer significant structural damage if struck, and rebuilding is a substantial capital undertaking.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Processing facility",
    hazard: "Heat Stress",
    score: 2,
    justification: "Uninsulated steel-framed sheds and equipment housings, typical of mid-scale regional processing facilities, wear faster under sustained heat, raising routine maintenance costs.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Processing facility",
    hazard: "Changing Precipitation",
    score: 1,
    justification: "Processing buildings are generally more solidly built than storage sheds and sustain limited physical damage from rainfall variability.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Processing facility",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Processing facility",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Processing facility",
    hazard: "Floods",
    score: 3,
    justification: "Inundation of ground-floor equipment and electrical systems requires cleaning, drying and part replacement before the plant can safely restart.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Processing facility",
    hazard: "Landslide",
    score: 4,
    justification: "Milling and manufacturing equipment is costly and slow to replace, and structural damage to the building can require extensive and expensive rebuilding.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agro-input facility",
    hazard: "Heat Stress",
    score: 2,
    justification: "Uninsulated warehouse storage accelerates degradation of stored seed and some agrochemicals, occasionally requiring stock write-off rather than structural repair.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agro-input facility",
    hazard: "Changing Precipitation",
    score: 3,
    justification: "Roof and wall leaks typical of basic industrial-shed construction let in moisture that damages stored seed and cakes fertilizer, requiring roofing repair and stock disposal.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agro-input facility",
    hazard: "Water Stress",
    score: 1,
    justification: "No physical damage pathway to the facility itself from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agro-input facility",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agro-input facility",
    hazard: "Floods",
    score: 4,
    justification: "At-grade warehouse storage is vulnerable to inundation, damaging flooring, electrical systems and stock, and creating a chemical-contamination clean-up requirement.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Agro-input facility",
    hazard: "Landslide",
    score: 3,
    justification: "Structural damage to a block or steel-shed facility requires wall and roof repair, and any chemical spillage from damaged containers adds a safety clean-up cost.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Livestock facility",
    hazard: "Heat Stress",
    score: 2,
    justification: "Corrugated-iron roofing on typical open-sided sheds heats up and can warp over time, occasionally requiring roofing repair or the addition of shading/ventilation improvements.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Livestock facility",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Open-sided structures allow wind-driven rain to damage flooring and bedding areas, requiring periodic repair and drainage improvement.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Livestock facility",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway to the structure from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Livestock facility",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Livestock facility",
    hazard: "Floods",
    score: 3,
    justification: "Facilities sited near water sources for convenience are prone to flooding, which can damage flooring, fencing and water/dip infrastructure, requiring rebuilding.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Livestock facility",
    hazard: "Landslide",
    score: 3,
    justification: "Lightweight post-and-sheet construction offers little structural protection if struck, but the structure itself is relatively inexpensive and quick to rebuild compared with masonry facilities.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Aggregation point",
    hazard: "Heat Stress",
    score: 1,
    justification: "Simple, largely open-air aggregation structures sustain negligible physical damage from heat.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Aggregation point",
    hazard: "Changing Precipitation",
    score: 1,
    justification: "Low-cost, largely open-air structures are not materially damaged by shifting rainfall patterns.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Aggregation point",
    hazard: "Water Stress",
    score: 1,
    justification: "No physical damage pathway from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Aggregation point",
    hazard: "Drought",
    score: 1,
    justification: "No physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Aggregation point",
    hazard: "Floods",
    score: 2,
    justification: "At-grade, low-cost structures can be damaged or washed out by flooding, though rebuilding cost is modest given the low capital investment involved.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Aggregation point",
    hazard: "Landslide",
    score: 2,
    justification: "Given the light construction typical of aggregation points, physical damage is disruptive but inexpensive and quick to repair or reinstate.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Logistics hub",
    hazard: "Heat Stress",
    score: 2,
    justification: "Depot roofing and any cold-chain equipment wear faster under sustained heat, raising facility maintenance costs.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Logistics hub",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Depot drainage and roofing require more frequent repair as rainfall intensity increases.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Logistics hub",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Logistics hub",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Logistics hub",
    hazard: "Floods",
    score: 4,
    justification: "At-grade depots suffer structural water damage to flooring, walls and electrical systems, and stored goods are often a total loss, together requiring substantial repair investment.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Logistics hub",
    hazard: "Landslide",
    score: 3,
    justification: "As a higher-value facility than a simple aggregation point, structural damage represents a more significant capital repair cost.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Office / headquarters",
    hazard: "Heat Stress",
    score: 1,
    justification: "Solid concrete or block office construction sustains no material damage from heat.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Office / headquarters",
    hazard: "Changing Precipitation",
    score: 1,
    justification: "Well-engineered urban commercial buildings sustain minimal physical damage from rainfall variability.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Office / headquarters",
    hazard: "Water Stress",
    score: 1,
    justification: "No physical damage pathway from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Office / headquarters",
    hazard: "Drought",
    score: 1,
    justification: "No physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Office / headquarters",
    hazard: "Floods",
    score: 2,
    justification: "Ground-floor or basement office space in flood-prone urban districts can suffer water damage to finishes, wiring and equipment, requiring localised repair.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Office / headquarters",
    hazard: "Landslide",
    score: 1,
    justification: "Solid, engineered, typically multi-storey construction in flatter commercial districts sustains limited damage even in the rare event of an impact.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Selling point",
    hazard: "Heat Stress",
    score: 1,
    justification: "Market stalls, kiosks and small retail shops - the typical regional selling point - sustain negligible structural damage from heat, beyond added wear on any refrigeration or display units.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Selling point",
    hazard: "Changing Precipitation",
    score: 2,
    justification: "Simple sheet-metal or tarpaulin roofing common on stalls and kiosks is prone to leaks under intense rainfall, requiring frequent patching or re-covering.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Selling point",
    hazard: "Water Stress",
    score: 1,
    justification: "No direct physical damage pathway from water scarcity.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Selling point",
    hazard: "Drought",
    score: 1,
    justification: "No direct physical damage pathway from drought.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Selling point",
    hazard: "Floods",
    score: 3,
    justification: "At-grade stalls, kiosks and small shops with earthen or basic concrete flooring are easily inundated, damaging stock, fittings and any electrical equipment and often requiring a full clean-out and partial rebuild.",
  },
  {
    sector: "agriculture",
    assetTypeName: "Selling point",
    hazard: "Landslide",
    score: 1,
    justification: "Given the light, low-cost construction typical of selling points, physical damage is disruptive but inexpensive and quick to repair or reinstate at an alternate site.",
  },
] as const;
